import { FormControlLabel as MuiFormControlLabel } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { COLORS, styled } from '../../theme'
import { Box } from '../Box'
import { Divider } from '../Divider'
import { Grid } from '../Grid'
import { Typography } from '../Typography'
import { Checkbox } from './Checkbox'

const StyledContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledHorizontalContainer = styled(Box)`
  display: flex;
  align-items: center;
`

const LabelContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
`
const HorizontalLabelContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: max-content;
`

const Wrapper = styled('div')`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HorizontalWrapper = styled('div')`
  padding: 0;
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
`

const StyledControlLabel = styled(MuiFormControlLabel)`
  padding-left: 10px;
  gap: 8px;
`
const StyledHorizontalControlLabel = styled(MuiFormControlLabel)`
  padding-left: 10px;
`

const GridContainer = styled(Grid)`
  column-gap: 16px;
`

type ListItem = {
  title: string
  subtitle?: string
  value: string
  disabled?: boolean
}

type Props = {
  disabled?: boolean
  onChange: (values: string[]) => void
  options: ListItem[]
  separator?: boolean
  values: string[]
  variant?: 'vertical' | 'horizontal'
  allLabel?: string
}

export function CheckboxMultiple(props: Props) {
  const { disabled, onChange, options, separator = true, values, variant = 'vertical', allLabel } = props

  const [allChecked, setAllChecked] = useState<boolean>(false)

  const isChecked = (value: string) => values.includes(value)

  const handleAllCheck = () => {
    const newCheckedItems = allChecked ? [] : options.map((e) => e.value)
    onChange(newCheckedItems)
    setAllChecked(!allChecked)
  }

  const handleCheck = (value: string) => {
    const newCheckedItems = isChecked(value) ? values.filter((item) => item !== value) : [...values, value]
    onChange(newCheckedItems)
  }

  useEffect(() => {
    setAllChecked(values.length === options.length && options.length > 0)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  return (
    <>
      {typeof allLabel !== 'undefined' && variant === 'vertical' && (
        <Wrapper>
          <StyledControlLabel
            disabled={disabled}
            label={allLabel}
            control={
              <Checkbox
                disabled={disabled}
                checked={allChecked}
                onChange={handleAllCheck}
                indeterminate={values.length > 0 && values.length < options.length}
                data-test-id="checkbox-multiple-all"
              />
            }
          />
        </Wrapper>
      )}
      {typeof allLabel !== 'undefined' && variant === 'horizontal' && (
        <HorizontalWrapper>
          <StyledHorizontalControlLabel
            disabled={disabled}
            label={allLabel}
            color={disabled ? COLORS.text.disabled : COLORS.text.primary}
            control={
              <Checkbox
                disabled={disabled}
                checked={allChecked}
                onChange={handleAllCheck}
                indeterminate={values.length > 0 && values.length < options.length}
                data-test-id="checkbox-multiple-all"
              />
            }
          />
        </HorizontalWrapper>
      )}
      {typeof allLabel !== 'undefined' && separator && <Divider />}
      {variant === 'vertical' && (
        <Wrapper>
          {options.map((e) => (
            <StyledContainer key={crypto.randomUUID()}>
              <Checkbox
                disabled={disabled}
                checked={isChecked(e.value)}
                onChange={() => handleCheck(e.value)}
                data-test-id="checkbox-multiple-item"
              />
              <LabelContainer>
                <Typography variant="subtitle1">{e.title}</Typography>
                <Typography variant="body1">{e.subtitle}</Typography>
              </LabelContainer>
            </StyledContainer>
          ))}
        </Wrapper>
      )}
      {variant === 'horizontal' && (
        <HorizontalWrapper>
          <GridContainer container>
            {options.map((e) => (
              <Grid key={crypto.randomUUID()}>
                <StyledHorizontalContainer>
                  <Checkbox
                    disabled={disabled}
                    checked={isChecked(e.value)}
                    onChange={() => handleCheck(e.value)}
                    data-test-id="checkbox-multiple-item"
                  />
                  <HorizontalLabelContainer>
                    <Typography variant="body1" color={disabled ? COLORS.text.disabled : COLORS.text.primary}>
                      {e.title}
                    </Typography>
                    {e.subtitle && <Typography variant="body1">{e.subtitle}</Typography>}
                  </HorizontalLabelContainer>
                </StyledHorizontalContainer>
              </Grid>
            ))}
          </GridContainer>
        </HorizontalWrapper>
      )}
    </>
  )
}
